import React, { useState, useEffect, useLayoutEffect, useRef, forwardRef, useImperativeHandle } from "react";
import * as Mui from "@mui/material";
import { makeStyles } from "tss-react/mui";
import type { WithStyles } from "type/tss";
import * as Icons from "react-feather";
import { useSelector } from "react-redux";
import { useAppThunkDispatch } from "redux/store";
import { Agent } from "model/agent";
import { updateAgent, deleteAgent } from "redux/slice/agents";
import { fetchAgentMlsIds } from "redux/slice/agent-mls-ids";
import { getUser } from "redux/selector";
import { Switch } from "component/shared/switch";
import { LabeledField } from "./labeled-field";
import { mlsSetupRefresh } from "shared/env";
import { RemoveMemberModal } from "./remove-member-modal";
import { styles } from "./styles";
import { TabHandle } from "./tab-handle";
import { baseColors } from "theme";

interface Props extends WithStyles<typeof styles> {
	agent: Agent;
	onDirtyChange: () => void;
	/** The logged-in member viewing their own Profile page (MAX-4139). */
	selfView?: boolean;
	/** Account has no team context — no Roles section to show. */
	isSingleAgent?: boolean;
}

const useLocalStyles = makeStyles()((theme) => ({
	// Block-level so each option stacks on its own row, even short ones like Permissions (MAX-3865).
	checkboxOption: {
		display: "flex", width: "fit-content", alignItems: "flex-start", marginBottom: theme.spacing(2), marginLeft: 0,
		// A locked option keeps its label color; only the box shows it is disabled (MAX-4509).
		"& .MuiFormControlLabel-label.Mui-disabled": { color: "inherit" },
	},
	checkbox: {
		padding: 0,
		marginTop: 2,
		marginRight: theme.spacing(1),
		color: theme.palette.custom.primary,
		"& svg": { fontSize: 24 },
		"&.Mui-checked": { color: theme.palette.custom.primary },
		"&.Mui-disabled": { color: baseColors.gray200 },
	},
	// The design kit's disabled checkbox: gray box, darker gray check. MUI's icon is a filled square
	// with the check cut out, so it can't take two colors (MAX-4509).
	disabledBox: {
		display: "inline-flex", alignItems: "center", justifyContent: "center",
		width: 18, height: 18, margin: 3, borderRadius: 2,
		backgroundColor: baseColors.gray100, color: baseColors.gray600,
	},
	optionTitle: { fontWeight: 700 },
	// Descriptions use primary text color everywhere (MAX-3866).
	optionDescription: { color: theme.palette.custom.textPrimary },
	removeWarning: {
		display: "flex", alignItems: "center", gap: 8,
		color: theme.palette.custom.textSecondary, marginBottom: theme.spacing(2),
	},
	removeWarningIcon: {
		color: theme.palette.secondary.main,
		flexShrink: 0,
	},
}));

export const AccessTab = forwardRef<TabHandle, Props>(({ agent, selfView = false, isSingleAgent = false, classes = {}, onDirtyChange }, ref) => {
	const { classes: local } = useLocalStyles();
	const dispatch = useAppThunkDispatch();
	const user = useSelector(getUser);
	// Two independent locks on the Admin checkbox. The account owner keeps the role because the
	// server enforces it (MAX-4140); nobody can remove their own, because a different admin has to
	// do it for them (MAX-4139). The owner's message wins — it names the action that resolves it.
	//
	// Deliberately not scoped to selfView: opening your own row from the team roster is still you
	// removing your own admin, and that should be blocked wherever it is attempted. This does reach
	// the member-detail view in production ahead of the flag.
	const isSelf = user.agentId === agent.id;
	const isAccountOwner = !!agent.isAccountOwner;
	const adminLocked = isAccountOwner || isSelf;
	const adminLockReason = isAccountOwner
		? "Account owners can't have admin access removed. Contact support to change account ownership."
		: "You can't remove your own Admin role. Another admin on the account can do this for you.";
	// Roles are hidden for non-admins and single-agent accounts in self-view; on the member-detail
	// view they are always shown.
	const showRoles = !selfView || (user.admin && !isSingleAgent);
	// In self-view the listing-alert toggle moves to its own Emails tab, per the MAX-4139 designs.
	const showListingAlertEmails = !selfView;
	// The designs have no Permissions section on self-view, for admins included (MAX-4487).
	const showPermissions = !selfView;
	// The server clears the password on every response, so a blank field means "keep the current
	// one". It is only required when this save creates the login. Username is required once a login
	// exists, which in self-view it always does — matching the legacy login form.
	const hasLogin = selfView || !!agent.username;

	const [username, setUsername] = useState(agent.username || "");
	const [password, setPassword] = useState("");
	const [showPassword, setShowPassword] = useState(false);
	// Username + password side by side reads as a sign-in form, and browsers autofill the admin's own
	// saved credentials into a member with none, which then save onto that member (MAX-4500). Keep the
	// inputs read-only until the admin actually clicks or tabs into one — autofill skips read-only fields.
	const [credentialsUnlocked, setCredentialsUnlocked] = useState(false);
	const unlockCredentials = () => setCredentialsUnlocked(true);
	const [listingAlertEmails, setListingAlertEmails] = useState(agent.sendEmailUpdatesToAgent || false);
	const [ownsLeads, setOwnsLeads] = useState(agent.ownsLeads || false);
	const [admin, setAdmin] = useState(agent.admin || false);
	// Bound directly to allowModify* (already allow-semantics) — no migration.
	const [allowModifySubscribers, setAllowSubs] = useState(agent.allowModifySubscribers ?? true);
	const [allowModifyListings, setAllowListings] = useState(agent.allowModifyListings ?? true);
	const [allowModifyBio, setAllowBio] = useState(agent.allowModifyBio ?? true);
	const [removeOpen, setRemoveOpen] = useState(false);
	const [usernameError, setUsernameError] = useState<string | null>(null);
	const [passwordError, setPasswordError] = useState<string | null>(null);

	// Toggling Lead Owner mounts/unmounts the Permissions section below, shifting everything under it
	// and making the page appear to jump (MAX-3864). Capture the scroll position on that toggle and
	// restore it after the DOM updates so the Roles section stays visually anchored where it was.
	const scrollRestoreRef = useRef<number | null>(null);
	const handleOwnsLeadsChange = (checked: boolean) => {
		scrollRestoreRef.current = window.scrollY;
		setOwnsLeads(checked);
	};
	useLayoutEffect(() => {
		if (scrollRestoreRef.current !== null) {
			window.scrollTo({ top: scrollRestoreRef.current });
			scrollRestoreRef.current = null;
		}
	}, [ownsLeads]);

	const isDirty = () =>
		username !== (agent.username || "") ||
		password !== "" ||
		listingAlertEmails !== (agent.sendEmailUpdatesToAgent || false) ||
		ownsLeads !== (agent.ownsLeads || false) ||
		admin !== (agent.admin || false) ||
		allowModifySubscribers !== (agent.allowModifySubscribers ?? true) ||
		allowModifyListings !== (agent.allowModifyListings ?? true) ||
		allowModifyBio !== (agent.allowModifyBio ?? true);

	useEffect(() => { onDirtyChange(); }, [
		onDirtyChange, username, password, listingAlertEmails, ownsLeads, admin,
		allowModifySubscribers, allowModifyListings, allowModifyBio,
	]);

	const validateLength = (value: string, label: string) =>
		value.length < 4 || value.length > 75 ? `${label} must be 4-75 characters` : null;

	const validate = () => {
		let nextUsernameError: string | null = null;
		let nextPasswordError: string | null = null;
		if (!username) {
			if (hasLogin || password) {
				nextUsernameError = "Username is required";
			}
		} else if (username !== (agent.username || "")) {
			nextUsernameError = validateLength(username, "Username");
		}
		if (!password) {
			if (!hasLogin && username) {
				nextPasswordError = "Password is required";
			}
		} else {
			nextPasswordError = validateLength(password, "Password");
		}
		setUsernameError(nextUsernameError);
		setPasswordError(nextPasswordError);
		return !nextUsernameError && !nextPasswordError;
	};

	const handleUsernameChange = (value: string) => {
		setUsername(value);
		setUsernameError(null);
	};

	const handlePasswordChange = (value: string) => {
		setPassword(value);
		setPasswordError(null);
	};

	useImperativeHandle(ref, () => ({
		isDirty,
		validate,
		cancel: () => {
			setUsername(agent.username || "");
			setPassword("");
			setUsernameError(null);
			setPasswordError(null);
			setListingAlertEmails(agent.sendEmailUpdatesToAgent || false);
			setOwnsLeads(agent.ownsLeads || false);
			setAdmin(agent.admin || false);
			setAllowSubs(agent.allowModifySubscribers ?? true);
			setAllowListings(agent.allowModifyListings ?? true);
			setAllowBio(agent.allowModifyBio ?? true);
		},
		save: async () => {
			const ownsLeadsChanged = ownsLeads !== (agent.ownsLeads || false);
			// .unwrap() so an API failure rejects here instead of resolving into a false success.
			await dispatch(updateAgent({
				agent: {
					...agent,
					username: username || agent.username,
					password: password || agent.password,
					// MAX-4318: dirty-tracked but omitted here, so it silently never saved.
					sendEmailUpdatesToAgent: listingAlertEmails,
					ownsLeads,
					admin,
					allowModifySubscribers,
					allowModifyListings,
					allowModifyBio,
				},
			})).unwrap();
			// Lead Owner is what makes an agent billable, and the agent MLS IDs carry each agent's billable. Reload
			// them so the Profile tab's MLS IDs and the summary match the saved Lead Owner.
			if (ownsLeadsChanged && mlsSetupRefresh && user.permissions.officeAccountType === true) {
				dispatch(fetchAgentMlsIds());
			}
			setPassword("");
		},
	}));

	const handleRemove = async () => {
		await dispatch(deleteAgent({ agent }));
		setRemoveOpen(false);
	};

	return (
		<div>
			{/* Login */}
			<div className={classes.section}>
				<Mui.Typography className={classes.sectionTitle}>Login</Mui.Typography>
				<Mui.Grid container spacing={2} className={classes.formColumn}>
					<Mui.Grid item xs={12}>
						<LabeledField
							label="Username"
							required
							value={username}
							onChange={handleUsernameChange}
							error={!!usernameError}
							helperText={usernameError}
							autoComplete="off"
							readOnly={!credentialsUnlocked}
							onInteract={unlockCredentials}
						/>
					</Mui.Grid>
					<Mui.Grid item xs={12}>
						<LabeledField
							label="Password"
							required
							type={showPassword ? "text" : "password"}
							value={password}
							onChange={handlePasswordChange}
							error={!!passwordError}
							helperText={passwordError}
							placeholder={hasLogin ? "********" : undefined}
							autoComplete="new-password"
							readOnly={!credentialsUnlocked}
							onInteract={unlockCredentials}
							endAdornment={
								<Mui.IconButton size="small" onClick={() => setShowPassword((s) => !s)}>
									{showPassword ? <Icons.EyeOff size={18} /> : <Icons.Eye size={18} />}
								</Mui.IconButton>
							}
						/>
					</Mui.Grid>
				</Mui.Grid>
			</div>

			{/* Listing Alert Emails — UI only (behavior blocked). Moves to the Emails tab in self-view. */}
			{showListingAlertEmails && (
			<div className={classes.section}>
				<Mui.Typography className={classes.sectionTitle}>Listing Alert Emails</Mui.Typography>
				<Switch
					checked={listingAlertEmails}
					label="Receive copies of listing alert emails sent to leads"
					description="When enabled, you will receive a copy of every listing alert email sent to your leads. Listing alerts notify leads of new listings that match their saved searches."
					onChange={setListingAlertEmails}
				/>
			</div>

			)}

			{/* Roles */}
			{showRoles && (
			<div className={classes.section}>
				<Mui.Typography className={classes.sectionTitle}>Roles</Mui.Typography>
				<Mui.FormControlLabel
					className={local.checkboxOption}
					control={<Mui.Checkbox className={local.checkbox} disableRipple checked={ownsLeads} onChange={(e) => handleOwnsLeadsChange(e.target.checked)} />}
					label={
						<div>
							<Mui.Typography className={local.optionTitle}>Lead Owner (Billable)</Mui.Typography>
							<Mui.Typography variant="body2" className={local.optionDescription}>
								Lead Owners have access to their own leads, listings, and performance dashboard.
							</Mui.Typography>
						</div>
					}
				/>
				{/* Tooltip wraps the label, not the checkbox — a disabled input swallows hover. */}
				<Mui.Tooltip title={adminLocked ? adminLockReason : ""}>
					<Mui.FormControlLabel
						className={local.checkboxOption}
						control={
							<Mui.Checkbox
								className={local.checkbox}
								disableRipple
								checked={admin}
								disabled={adminLocked}
								onChange={(e) => setAdmin(e.target.checked)}
								{...(adminLocked && {
									icon: <span className={local.disabledBox} />,
									checkedIcon: <span className={local.disabledBox}><Icons.Check size={14} strokeWidth={3} /></span>,
								})}
							/>
						}
						label={
							<div>
								<Mui.Typography className={local.optionTitle}>Admin</Mui.Typography>
								<Mui.Typography variant="body2" className={local.optionDescription}>
									Admins have full access to all parts of the Control Panel, including team settings, all leads and listings, and account configuration.
								</Mui.Typography>
							</div>
						}
					/>
				</Mui.Tooltip>
			</div>
			)}

			{/* Permissions — visible only for Lead Owner (billable) members */}
			{showPermissions && ownsLeads && (
				<div className={classes.section}>
					<Mui.Typography className={classes.sectionTitle}>Permissions</Mui.Typography>
					<Mui.Typography className={classes.sectionDescription}>Allow agent to modify:</Mui.Typography>
					<Mui.FormControlLabel
						className={local.checkboxOption}
						control={<Mui.Checkbox className={local.checkbox} disableRipple checked={allowModifySubscribers} onChange={(e) => setAllowSubs(e.target.checked)} />}
						label={
							<div>
								<Mui.Typography className={local.optionTitle}>Lead subscriptions</Mui.Typography>
								<Mui.Typography variant="body2" className={local.optionDescription}>Agent can subscribe leads to marketing campaigns and market reports.</Mui.Typography>
							</div>
						}
					/>
					<Mui.FormControlLabel
						className={local.checkboxOption}
						control={<Mui.Checkbox className={local.checkbox} disableRipple checked={allowModifyListings} onChange={(e) => setAllowListings(e.target.checked)} />}
						label={
							<div>
								<Mui.Typography className={local.optionTitle}>Listings</Mui.Typography>
								<Mui.Typography variant="body2" className={local.optionDescription}>Agent can update their listing information.</Mui.Typography>
							</div>
						}
					/>
					<Mui.FormControlLabel
						className={local.checkboxOption}
						control={<Mui.Checkbox className={local.checkbox} disableRipple checked={allowModifyBio} onChange={(e) => setAllowBio(e.target.checked)} />}
						label={
							<div>
								<Mui.Typography className={local.optionTitle}>Public Bio</Mui.Typography>
								<Mui.Typography variant="body2" className={local.optionDescription}>Agent can update their own public bio that displays on the website.</Mui.Typography>
							</div>
						}
					/>
				</div>
			)}

			{/* Remove Team Member — admin only, not self */}
			{user.admin && user.agentId !== agent.id && (
				<div className={classes.section}>
					<Mui.Typography className={classes.sectionTitle}>Remove Team Member</Mui.Typography>
					<div className={local.removeWarning}>
						<Icons.AlertTriangle size={16} className={local.removeWarningIcon} />
						<Mui.Typography variant="body2">
							Permanently removes this member's access to iHomefinder. Their leads will be unassigned.
						</Mui.Typography>
					</div>
					{/* Span wrapper so the tooltip still fires once the button is disabled. */}
					<Mui.Tooltip
						title={isAccountOwner ? "Account owners can't be removed. Contact support to change account ownership." : ""}
					>
						<span>
							<Mui.Button variant="contained" color="primary" disabled={isAccountOwner} onClick={() => setRemoveOpen(true)}>Remove Team Member</Mui.Button>
						</span>
					</Mui.Tooltip>
				</div>
			)}

			<RemoveMemberModal open={removeOpen} agent={agent} onCancel={() => setRemoveOpen(false)} onConfirm={handleRemove} />
		</div>
	);
});
